import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { artistsApi, contactsApi } from "../../api";
import { DirectoryDrawer } from "../../components/directory/DirectoryDrawer";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { DirectoryAvatar } from "../../components/directory/DirectoryAvatar";
import { PhotoUploadField } from "../../components/directory/PhotoUploadField";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { QueryErrorBanner, ListSkeletonCards } from "../../components/feedback/QueryFeedback";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { Artist, Contact } from "../../types";
import {
  buildContactPayload,
  CONTACT_ROLES,
  contactToForm,
  emptyContactForm,
  formatContactRole,
  formatContactSummary,
  type ContactFormState,
} from "../../utils/contactProfile";
import { artistDisplayName } from "../../utils/artistProfile";
import { formatLinkedArtists } from "../../utils/contactForm";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import "../../components/directory/directoryList.css";
import "./ContactsPage.css";

type Props = {
  workspace: WorkspaceId;
};

const ContactsPage: React.FC<Props> = ({ workspace }) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ContactFormState>(emptyContactForm());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Contact | null>(null);
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string | null>(null);

  const patchForm = (patch: Partial<ContactFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const contactsQuery = useQuery({
    queryKey: token ? queryKeys.contacts.list(workspace) : queryKeys.contacts.all(workspace),
    queryFn: () => contactsApi.list(token!),
    enabled: Boolean(token),
  });

  const artistsQuery = useQuery({
    queryKey: token ? queryKeys.artists.list(workspace) : queryKeys.artists.all(workspace),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.contacts.list(workspace) });
  };

  const createMutation = useMutation({
    mutationFn: async (payload: ContactFormState) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return contactsApi.create(buildContactPayload(payload), token);
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; formData: ContactFormState }) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return contactsApi.update(payload.id, buildContactPayload(payload.formData), token);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return contactsApi.remove(id, token);
    },
    onSuccess: invalidate,
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!token || !editingId) throw new Error("Save the contact before uploading a photo.");
      return contactsApi.uploadPhoto(editingId, file, token);
    },
    onSuccess: async (updated) => {
      setUploadedPhotoUrl(updated.photo_url ?? null);
      await invalidate();
    },
  });

  const rows = useMemo<Contact[]>(() => contactsQuery.data ?? [], [contactsQuery.data]);
  const artists = useMemo<Artist[]>(() => artistsQuery.data ?? [], [artistsQuery.data]);
  const loading = contactsQuery.isPending;
  const errorMsg =
    contactsQuery.error instanceof Error ? contactsQuery.error.message : contactsQuery.error ? String(contactsQuery.error) : "";

  const resetForm = () => {
    setForm(emptyContactForm());
    setEditingId(null);
    setFormOpen(false);
    setUploadedPhotoUrl(null);
  };

  const openCreate = () => {
    setForm(emptyContactForm());
    setEditingId(null);
    setFormOpen(true);
    setUploadedPhotoUrl(null);
  };

  const beginEdit = (contact: Contact) => {
    setEditingId(contact.id);
    setForm(contactToForm(contact));
    setFormOpen(true);
    setUploadedPhotoUrl(contact.photo_url ?? null);
  };

  const editingContact = editingId ? rows.find((contact) => contact.id === editingId) ?? null : null;
  const currentPhotoUrl = uploadedPhotoUrl ?? editingContact?.photo_url ?? null;

  const toggleArtist = (artistId: string) => {
    setForm((prev) => {
      const has = prev.artist_ids.includes(artistId);
      return {
        ...prev,
        artist_ids: has ? prev.artist_ids.filter((id) => id !== artistId) : [...prev.artist_ids, artistId],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, formData: form });
        toast.success("Contact updated");
      } else {
        await createMutation.mutateAsync(form);
        toast.success("Contact created");
      }
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : editingId ? "Failed to update contact" : "Failed to create contact");
    }
  };

  const handleDelete = (contact: Contact) => {
    setPendingDelete(contact);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      if (editingId === pendingDelete.id) resetForm();
      toast.success("Contact deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete contact");
    } finally {
      setPendingDelete(null);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="app-shell-dash contacts-page">
      <div className="app-shell-dash-toolbar">
        <div>
          <h1 className="app-shell-page-title contacts-page__title">Contacts</h1>
          <p className="app-shell-lede contacts-page__lede">
            Agents, managers, and co-promoters linked to artists for quick reach on show dashboards.
          </p>
        </div>
        <button type="button" className="eventos-primary-btn" onClick={openCreate}>
          Add contact
        </button>
      </div>

      {contactsQuery.isError ? (
        <QueryErrorBanner
          message={errorMsg || "Unable to load contacts"}
          onRetry={() => void contactsQuery.refetch()}
          retrying={contactsQuery.isFetching}
        />
      ) : null}

      <div className="contacts-page__layout directory-page__list-only">
        <div className="directory-page__list-bar">
          <span className="directory-page__count">
            {loading ? "Loading…" : `${rows.length} contact${rows.length === 1 ? "" : "s"}`}
          </span>
          <button type="button" className="eventos-nav-btn" onClick={() => void contactsQuery.refetch()} disabled={contactsQuery.isFetching}>
            {contactsQuery.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <div className="directory-page__list">
          {loading ? (
            <ListSkeletonCards rows={6} />
          ) : rows.length === 0 ? (
            <p className="directory-page__empty">No contacts yet. Add your first contact to get started.</p>
          ) : (
            rows.map((contact) => {
              return (
                <article
                  key={contact.id}
                  className={`directory-page__row${editingId === contact.id ? " is-editing" : ""}`}
                >
                  <DirectoryAvatar name={contact.name} photoUrl={contact.photo_url} className="directory-page__avatar" />
                  <div className="directory-page__row-main">
                    <div className="directory-page__row-head">
                      <span className="directory-page__row-name">{contact.name}</span>
                      {contact.role ? (
                        <span className="directory-page__pill">{formatContactRole(contact.role)}</span>
                      ) : null}
                      {contact.agency ? (
                        <span className="directory-page__pill directory-page__pill--muted">{contact.agency}</span>
                      ) : null}
                    </div>
                    <div className="directory-page__row-details">
                      <div className="directory-page__detail">
                        <span className="directory-page__detail-label">Reach</span>
                        <span className="directory-page__detail-value directory-page__detail-value--muted">
                          {formatContactSummary(contact)}
                        </span>
                      </div>
                      <div className="directory-page__detail">
                        <span className="directory-page__detail-label">Artists</span>
                        <span className="directory-page__detail-value">{formatLinkedArtists(contact, artists)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="directory-page__row-actions">
                    <button type="button" className="eventos-nav-btn" onClick={() => beginEdit(contact)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="eventos-nav-btn"
                      onClick={() => handleDelete(contact)}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>

      <DirectoryDrawer
        open={formOpen}
        size="form"
        placement="center"
        title={editingId ? "Edit contact" : "Add contact"}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="eventos-form directory-page__drawer-form">
          <div className="app-form-section" role="group" aria-labelledby="contact-sec-profile">
            <div id="contact-sec-profile" className="app-form-section-title">
              Profile
            </div>
            {editingId ? (
              <PhotoUploadField
                name={form.name || "Contact"}
                photoUrl={currentPhotoUrl}
                uploading={uploadPhotoMutation.isPending}
                onUpload={(file) => uploadPhotoMutation.mutateAsync(file)}
              />
            ) : null}
            <div className="eventos-form-grid">
              <label className="eventos-field">
                <span className="eventos-field-label">Name</span>
                <input
                  className="eventos-input"
                  value={form.name}
                  onChange={(e) => patchForm({ name: e.target.value })}
                  placeholder="Contact name"
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Role</span>
                <MobileSelect
                  label="Role"
                  className="eventos-select"
                  value={form.role}
                  onChange={(v) => patchForm({ role: v as ContactFormState["role"] })}
                  options={[
                    { value: "", label: "Select role (optional)" },
                    ...CONTACT_ROLES.map((role) => ({ value: role.value, label: role.label })),
                  ]}
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Agency / company</span>
                <input
                  className="eventos-input"
                  value={form.agency}
                  onChange={(e) => patchForm({ agency: e.target.value })}
                  placeholder="Agency or label (optional)"
                />
              </label>
            </div>
          </div>

          <div className="app-form-section" role="group" aria-labelledby="contact-sec-reach">
            <div id="contact-sec-reach" className="app-form-section-title">
              Reach
            </div>
            <div className="eventos-form-grid">
              <label className="eventos-field">
                <span className="eventos-field-label">Email</span>
                <input
                  className="eventos-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => patchForm({ email: e.target.value })}
                  placeholder="contact@agency.com"
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Phone</span>
                <input
                  className="eventos-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => patchForm({ phone: e.target.value })}
                  placeholder="Phone number"
                />
              </label>
            </div>
          </div>

          <div className="app-form-section" role="group" aria-labelledby="contact-sec-artists">
            <div id="contact-sec-artists" className="app-form-section-title">
              Linked artists
            </div>
            <p className="app-form-section-lede">Contacts appear on event dashboards when linked to that event&apos;s artist.</p>
            {artists.length === 0 ? (
              <p className="contacts-page__artist-hint">No artists in registry yet — add artists first to link them here.</p>
            ) : (
              <div className="contacts-page__artist-list" role="group" aria-label="Linked artists">
                {artists.map((artist) => (
                  <label key={artist.id} className="contacts-page__artist-option">
                    <input
                      type="checkbox"
                      checked={form.artist_ids.includes(artist.id)}
                      onChange={() => toggleArtist(artist.id)}
                    />
                    <span>{artistDisplayName(artist)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="app-form-section" role="group" aria-labelledby="contact-sec-notes">
            <div id="contact-sec-notes" className="app-form-section-title">
              Notes
            </div>
            <label className="eventos-field contacts-page__notes-field">
              <span className="eventos-field-label">Internal notes</span>
              <textarea
                className="eventos-input"
                rows={3}
                value={form.notes}
                onChange={(e) => patchForm({ notes: e.target.value })}
                placeholder="Contract notes, preferred channel, timezone…"
              />
            </label>
          </div>

          <div className="app-form-actions">
            <button className="eventos-primary-btn" type="submit" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Save changes" : "Create contact"}
            </button>
            <button type="button" className="eventos-nav-btn" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </DirectoryDrawer>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete contact?"
        message={pendingDelete ? `"${pendingDelete.name}" will be removed from your directory.` : ""}
        confirmLabel="Delete"
        danger
        loading={deleteMutation.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ContactsPage;
